import { useAppSelector as useSelector } from 'dux/app/hooks';

import { cartUrlFromCategory } from 'constants/cart';
import { productsCategories, productsSlugs } from 'constants/products';

import * as ctaContent from 'assets/content/consultation/cta';

import { getIsAuthenticated, getIsAuthenticating } from 'dux/auth/selectors';
import {
  getFirstHaircareConsultationQuestionRoute,
  getFirstSkincareConsultationQuestionRoute,
} from 'dux/consultation/selectors';
import * as userSelectors from 'dux/user/selectors';

const getRenderParams = ({
  hasCompletedHaircareConsultation,
  isAuthenticated,
  hasCompletedSkincareConsultation,
  productCategory,
  hasDraftHaircareConsultation,
  hasDraftSkincareConsultation,
  firstHaircareConsultationQuestionRoute,
  firstSkincareConsultationQuestionRoute,
  hasAnsweredSupplementsQuestions,
  hasAnsweredStylingGelQuestions,
  slug,
}) => {
  const productIsHaircareAndHasAnsweredQuestions =
    productCategory === productsCategories.HAIRCARE && hasCompletedHaircareConsultation;
  const productIsSkincareAndHasAnsweredQuestions =
    productCategory === productsCategories.SKINCARE && hasCompletedSkincareConsultation;
  const productIsHaircareAndHasDraftConsultation =
    productCategory === productsCategories.HAIRCARE && hasDraftHaircareConsultation;
  const productIsSkincareAndHasDraftConsultation =
    productCategory === productsCategories.SKINCARE && hasDraftSkincareConsultation;

  if (productCategory === productsCategories.OTHERS) {
    if (isAuthenticated) {
      if (hasCompletedHaircareConsultation) {
        return {
          to: '/checkout/haircare',
          label: 'Add to cart',
          dataClick: 'add-to-cart',
          shouldQueueBreadcrumb: true,
        };
      }
      return {
        to: '/signin-accessories-complete',
        label: 'Add to cart',
        dataClick: 'consultation',
        shouldQueueBreadcrumb: true,
      };
    }
    return {
      to: `/signin?from=${productCategory}`,
      label: 'Sign in/up to add',
      dataClick: 'sign-in',
      shouldQueueBreadcrumb: true,
    };
  }

  if (isAuthenticated) {
    if (productIsHaircareAndHasDraftConsultation || productIsSkincareAndHasDraftConsultation) {
      return {
        to:
          productCategory === productsCategories.SKINCARE
            ? firstSkincareConsultationQuestionRoute
            : firstHaircareConsultationQuestionRoute,
        label: ctaContent.oneConsultationStarted,
        dataClick: 'first-question-consultation',
        shouldQueueBreadcrumb: false,
      };
    }
    if (productIsHaircareAndHasAnsweredQuestions || productIsSkincareAndHasAnsweredQuestions) {
      if (
        !hasAnsweredSupplementsQuestions &&
        productIsHaircareAndHasAnsweredQuestions &&
        slug === productsSlugs.SUPPLEMENT_CORE
      ) {
        return {
          to: `/consultation/${productCategory}?questionSet=supplements`,
          label: ctaContent.newUserPDP,
          dataClick: `consultation-${productCategory}`,
          shouldQueueBreadcrumb: false,
        };
      }
      if (
        !hasAnsweredStylingGelQuestions &&
        productIsHaircareAndHasAnsweredQuestions &&
        slug === productsSlugs.STYLING_GEL
      ) {
        return {
          to: `/consultation/${productCategory}?questionSet=styling-gel`,
          label: ctaContent.newUserPDP,
          dataClick: `consultation-${productCategory}`,
          shouldQueueBreadcrumb: false,
        };
      }
      return {
        to: cartUrlFromCategory[productCategory],
        label: ctaContent.abandonedCart(),
        dataClick: `${productCategory}-cart`,
        shouldQueueBreadcrumb: true,
      };
    }
  }

  return {
    to: `/consultation/${productCategory}${
      slug === productsSlugs.SUPPLEMENT_CORE ? '?from=supplements-page' : ''
    }`,
    label: ctaContent.newUserPDP,
    dataClick: `consultation-${productCategory}`,
    shouldQueueBreadcrumb: false,
  };
};

function useMapSelectorsToState() {
  const hasCompletedHaircareConsultation = useSelector(
    userSelectors.getHasCompletedHaircareConsultation
  );
  const hasCompletedSkincareConsultation = useSelector(
    userSelectors.getHasCompletedSkincareConsultation
  );
  const hasDraftHaircareConsultation = useSelector(userSelectors.getHasDraftHaircareConsultation);
  const hasDraftSkincareConsultation = useSelector(userSelectors.getHasDraftSkincareConsultation);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const hasAnsweredSupplementsQuestions = useSelector(
    userSelectors.getHasAnsweredSupplementsQuestions
  );
  const hasAnsweredStylingGelQuestions = useSelector(
    userSelectors.getHasAnsweredStylingGelQuestions
  );
  const isAuthenticating = useSelector(getIsAuthenticating);
  const firstHaircareConsultationQuestionRoute = useSelector(
    getFirstHaircareConsultationQuestionRoute
  );
  const firstSkincareConsultationQuestionRoute = useSelector(
    getFirstSkincareConsultationQuestionRoute
  );

  return {
    hasCompletedHaircareConsultation,
    hasCompletedSkincareConsultation,
    isAuthenticated,
    isAuthenticating,
    hasDraftHaircareConsultation,
    hasDraftSkincareConsultation,
    firstHaircareConsultationQuestionRoute,
    firstSkincareConsultationQuestionRoute,
    hasAnsweredSupplementsQuestions,
    hasAnsweredStylingGelQuestions,
  };
}

export default function useProductDetailsCTA(
  { productName = null, slug = null, productCategory = null } = {},
  { useGlobalValues = useMapSelectorsToState } = {}
) {
  const {
    hasCompletedHaircareConsultation,
    hasCompletedSkincareConsultation,
    hasDraftHaircareConsultation,
    hasDraftSkincareConsultation,
    isAuthenticated,
    isAuthenticating,
    firstHaircareConsultationQuestionRoute,
    firstSkincareConsultationQuestionRoute,
    hasAnsweredSupplementsQuestions,
    hasAnsweredStylingGelQuestions,
  } = useGlobalValues();

  if (isAuthenticating) {
    return {
      to: '',
      label: '',
      dataClick: '',
      isLoading: true,
      shouldQueueBreadcrumb: false,
    };
  }

  return getRenderParams({
    hasCompletedHaircareConsultation,
    hasCompletedSkincareConsultation,
    hasDraftHaircareConsultation,
    hasDraftSkincareConsultation,
    isAuthenticated,
    productName,
    productCategory,
    firstHaircareConsultationQuestionRoute,
    firstSkincareConsultationQuestionRoute,
    slug,
    hasAnsweredSupplementsQuestions,
    hasAnsweredStylingGelQuestions,
  });
}
